import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { savePermitForm, updatePermitForm } from '../../store/slices/projectPermitsSlice';
import { showToast } from '../utils/Toast';
import { fetchProjectData, prefillFormData } from '../../utils/projectDataUtils';
import { printPermitForm, createCompanyHeader, formatFormField, formatTableRow } from '../../utils/PermitPrintUtils';
import FloatingActionBar from './FloatingActionBar';

const SlateForm = ({ projectId, currentPermit, onClose }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  
  // Define all the form fields based on the image
  const [formData, setFormData] = useState({
    projectName: '',
    jobAddress: '',
    jobNumber: '',
    date: '',
    items: {
      roofPitch: { quantity: '', notes: '' },
      accessDoubleHandle: { quantity: '', notes: '' },
      slateTypeAndQuantity: { quantity: '', notes: '' },
      slateColor: { quantity: '', notes: '' },
      ridgeAndHip: { quantity: '', notes: '' },
      underlayment: { quantity: '', notes: '' },
      roofingNailsStainless: { quantity: '', notes: '' },
      dripEdgeCopper: { quantity: '', notes: '' },
      capNailsAndSize: { quantity: '', notes: '' },
      batons: { quantity: '', notes: '' },
      tilePanCopper: { quantity: '', notes: '' },
      roofToWallCopper: { quantity: '', notes: '' },
      paintAndColor: { quantity: '', notes: '' },
      caulkingAndColor: { quantity: '', notes: '' },
      saddleCopper: { quantity: '', notes: '' },
      mastic: { quantity: '', notes: '' },
      ohagansAndDormers: { quantity: '', notes: '' },
      flashingCopper: { quantity: '', notes: '' },
      tTops: { quantity: '', notes: '' },
      twoInch: { quantity: '', notes: '' },
      threeInch: { quantity: '', notes: '' },
      fourInch: { quantity: '', notes: '' },
      rollOf6MilPlastic: { quantity: '', notes: '' }
    }
  });

  useEffect(() => {
    if (currentPermit && currentPermit.formType === 'slateForm') {
      setFormData(currentPermit.formData || formData);
    } else {
      // Fetch project data to prefill the form
      const getProjectData = async () => {
        try {
          // Get project data
          const projectData = await fetchProjectData(projectId);
          console.log('Project data for slate form:', projectData);
          
          // Handle the case where the project is nested in a 'project' property
          const actualProject = projectData.project || projectData;
          
          // Prefill the form data
          const prefilled = { ...formData };
          
          // Set project name
          if (actualProject.name) {
            prefilled.projectName = actualProject.name;
          }
          
          // Set job address
          if (actualProject.siteContact && actualProject.siteContact.address) {
            const address = actualProject.siteContact.address;
            let fullAddress = address.street || '';
            if (address.city) fullAddress += (fullAddress ? ', ' : '') + address.city;
            if (address.state) fullAddress += (fullAddress ? ', ' : '') + address.state;
            if (address.zipCode) fullAddress += (fullAddress ? ' ' : '') + address.zipCode;
            prefilled.jobAddress = fullAddress;
          } else if (actualProject.address) {
            const address = actualProject.address;
            let fullAddress = address.street || '';
            if (address.city) fullAddress += (fullAddress ? ', ' : '') + address.city;
            if (address.state) fullAddress += (fullAddress ? ', ' : '') + address.state;
            if (address.zipCode) fullAddress += (fullAddress ? ' ' : '') + address.zipCode;
            prefilled.jobAddress = fullAddress;
          }
          
          // Set today's date in YYYY-MM-DD format
          const now = new Date();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          const year = now.getFullYear();
          prefilled.date = `${year}-${month}-${day}`;
          
          // Set job number
          if (actualProject.jobNumber) {
            prefilled.jobNumber = actualProject.jobNumber;
          } else if (actualProject.originalLead && actualProject.originalLead.jobNumber) {
            prefilled.jobNumber = actualProject.originalLead.jobNumber;
          }
          
          console.log('Prefilled slate form data:', prefilled);
          setFormData(prefilled);
        } catch (error) {
          console.error('Error prefilling slate form data:', error);
        }
      };
      
      getProjectData();
    }
  }, [currentPermit, projectId]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleItemChange = (itemKey, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: {
        ...prev.items,
        [itemKey]: {
          ...prev.items[itemKey],
          [field]: value
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (currentPermit && currentPermit._id) {
        await dispatch(updatePermitForm({
          projectId,
          permitId: currentPermit._id,
          formData: {
            ...currentPermit,
            formData
          }
        })).unwrap();
        showToast('Slate Material Order updated successfully', 'success');
      } else {
        await dispatch(savePermitForm({
          projectId,
          formType: 'slateForm',
          formData
        })).unwrap();
        showToast('Slate Material Order saved successfully', 'success');
      }
      onClose();
    } catch (error) {
      showToast(error.message || 'Failed to save Slate Material Order', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format camelCase keys into readable labels
  const formatItemLabel = (key) => {
    // Special cases
    if (key === 'rollOf6MilPlastic') return 'Roll of 6 mil plastic';
    if (key === 'tTops') return 'T-tops';
    if (key === 'twoInch') return '2"';
    if (key === 'threeInch') return '3"';
    if (key === 'fourInch') return '4"';
    if (key === 'ohagansAndDormers') return "O'hagan and Dormers";
    if (key === 'flashingCopper') return 'Flashing(s): copper';
    if (key === 'accessDoubleHandle') return 'Access/Double handle';
    if (key === 'slateTypeAndQuantity') return 'Slate Type and quantity';
    if (key === 'slateColor') return 'Slate color';
    if (key === 'ridgeAndHip') return 'Ridge and Hip';
    if (key === 'roofingNailsStainless') return 'Roofing Nails: (stainless)';
    if (key === 'dripEdgeCopper') return 'Drip Edge (copper)';
    if (key === 'capNailsAndSize') return 'Cap nails and size';
    if (key === 'tilePanCopper') return 'Tile Pan: (copper)';
    if (key === 'roofToWallCopper') return 'Roof to Wall: (copper)';
    if (key === 'paintAndColor') return 'Paint and color';
    if (key === 'caulkingAndColor') return 'Caulking and color';
    if (key === 'saddleCopper') return 'Saddle: (copper)';
    
    // General case: convert camelCase to spaces and capitalize
    const withSpaces = key.replace(/([A-Z])/g, ' $1').trim();
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  };

  const handleDownload = () => {
    // Create a printable version of the form content
    const formContent = `
      ${createCompanyHeader()}
      <h1 class="form-title">Slate Material Order</h1>
      
      <div class="form-section">
        <div class="form-row">
          <div class="form-col">
            ${formatFormField('Project Name', formData.projectName)}
          </div>
          <div class="form-col">
            ${formatFormField('Job Address', formData.jobAddress)}
          </div>
        </div>
        <div class="form-row">
          <div class="form-col">
            ${formatFormField('Date', formData.date)}
          </div>
          <div class="form-col">
            ${formatFormField('Job Number', formData.jobNumber)}
          </div>
        </div>
      </div>
      
      <div class="form-section">
        <table class="form-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Quantity</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            ${Object.entries(formData.items).map(([key, value]) => `
              <tr>
                <td>${formatItemLabel(key)}</td>
                <td>${value.quantity || ''}</td>
                <td>${value.notes || ''}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
    
    // Print the form
    printPermitForm(formData, 'Slate Material Order', formContent);
  };

  return (
    <div className="permit-form-shell bg-white p-6 rounded-lg max-w-5xl mx-auto relative">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <img 
            src="/logo/JRT-logo-small.jpg" 
            alt="JRT Roofing & Waterproofing" 
            className="h-16 mr-4"
          />
          <div>
            <h2 className="text-2xl font-bold">JRT ROOFING & WATERPROOFING</h2>
            <p className="text-sm">QLTY CRAFTSMANSHIP</p>
          </div>
        </div>
        <div className="text-right">
          <p>P.O. Box 3688</p>
          <p>San Clemente, CA 92672</p>
          <p>Phone: (949) 742-2892</p>
          <p className="uppercase">JRTROOFING.COM</p>
          <p>CA 1070037</p>
        </div>
      </div>

      <h1 className="text-2xl font-bold mb-6 text-center">Slate Material Order</h1>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name:</label>
            <input
              type="text"
              value={formData.projectName}
              onChange={(e) => handleChange('projectName', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Address:</label>
            <input
              type="text"
              value={formData.jobAddress}
              onChange={(e) => handleChange('jobAddress', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date:</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => handleChange('date', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Number:</label>
            <input
              type="text"
              value={formData.jobNumber}
              onChange={(e) => handleChange('jobNumber', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
            />
          </div>
        </div>

        <div className="overflow-x-auto mb-6">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr>
                <th className="border border-gray-300 p-2 w-1/2 text-left">Item</th>
                <th className="border border-gray-300 p-2 w-1/2 text-center">Quantity</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(formData.items).map(([key, value]) => (
                <tr key={key}>
                  <td className="border border-gray-300 p-2 font-medium">
                    {formatItemLabel(key)}
                  </td>
                  <td className="border border-gray-300 p-2">
                    <input
                      type="text"
                      value={value.quantity}
                      onChange={(e) => handleItemChange(key, 'quantity', e.target.value)}
                      className="w-full p-1 border border-gray-200 rounded"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add padding at the bottom to ensure content isn't hidden behind the floating bar */}
        <div className="pb-20"></div>
      </form>
      
      {/* Floating Action Bar */}
      <FloatingActionBar 
        onCancel={onClose}
        onDownload={handleDownload}
        onSubmit={handleSubmit}
        loading={loading}
        isUpdate={!!(currentPermit && currentPermit._id)}
      />
    </div>
  );
};

export default SlateForm;
